/**
 * Nettoyage des « petits points » laissés par la suppression de couleurs :
 * après le détourage à la tolérance, il reste des îlots de quelques pixels
 * opaques dans le fond (et parfois des trous de quelques pixels dans le
 * dessin). On repère chaque zone connexe (8-voisinage) et on efface celles
 * qui sont plus petites que le seuil — le dessin principal n'est pas touché.
 *
 * 100 % navigateur, aucune API, aucun coût. Linéaire en nombre de pixels
 * (~0,35 s pour 4 Mpx, ~1 s pour 12 Mpx : à lancer sur un bouton, pas à chaque mouvement du curseur). Se branche juste après recomputeSession().
 */

/** Seuil par défaut : 0,005 % de l'image, au moins 6 px (≈ 600 px sur 12 Mpx). */
export function defaultSpeckleArea(w: number, h: number): number {
	return Math.max(6, Math.round(w * h * 0.00005));
}

/**
 * Efface les îlots opaques de moins de `minArea` pixels et, si `orig` est
 * fourni, rebouche les trous transparents de moins de `minArea` pixels en
 * reprenant l'alpha d'origine. Modifie `d` (RGBA) en place.
 * Retourne le nombre d'îlots et de trous traités.
 */
export function despeckle(
	d: Uint8ClampedArray,
	w: number,
	h: number,
	minArea: number,
	orig?: Uint8ClampedArray,
	/** couleurs retirées : si fournies, un petit îlot n'est effacé que si sa couleur moyenne est à moins de
	 *  √keyDist2 (distance RGB) de l'une d'elles — un détail du logo dans sa propre couleur est conservé */
	keys?: { r: number; g: number; b: number }[],
	keyDist2 = 0,
	/** en dessous de cette aire, un îlot est effacé quelle que soit sa couleur (grain de 1 à 3 px : rien d'imprimable) */
	tinyArea = 0,
): { islands: number; holes: number } {
	const n = w * h;
	const solid = new Uint8Array(n);
	for (let i = 0; i < n; i++) solid[i] = d[i * 4 + 3] >= 16 ? 1 : 0;
	const seen = new Uint8Array(n);
	const stack = new Int32Array(n);
	const comp: number[] = [];
	let islands = 0;
	let holes = 0;

	for (let start = 0; start < n; start++) {
		if (seen[start]) continue;
		const kind = solid[start];
		// Les trous ne se rebouchent que si l'on connaît l'original
		// et seulement s'ils ne touchent pas le bord (sinon c'est du fond).
		let sp = 0;
		stack[sp++] = start;
		seen[start] = 1;
		comp.length = 0;
		let touchesEdge = false;
		let big = false;
		while (sp) {
			const p = stack[--sp];
			if (!big) {
				comp.push(p);
				if (comp.length >= minArea) big = true;
			}
			const x = p % w;
			const y = (p - x) / w;
			if (x === 0 || y === 0 || x === w - 1 || y === h - 1) touchesEdge = true;
			for (let dy = -1; dy <= 1; dy++) {
				const yy = y + dy;
				if (yy < 0 || yy >= h) continue;
				for (let dx = -1; dx <= 1; dx++) {
					const xx = x + dx;
					if ((dx | dy) === 0 || xx < 0 || xx >= w) continue;
					const q = yy * w + xx;
					if (!seen[q] && solid[q] === kind) {
						seen[q] = 1;
						stack[sp++] = q;
					}
				}
			}
		}
		if (big) continue;
		if (kind === 1) {
			if (keys && keys.length && comp.length >= tinyArea) {
				let sr = 0;
				let sg = 0;
				let sb = 0;
				for (const p of comp) {
					sr += d[p * 4];
					sg += d[p * 4 + 1];
					sb += d[p * 4 + 2];
				}
				const mr = sr / comp.length;
				const mg = sg / comp.length;
				const mb = sb / comp.length;
				// couleur du fond retiré AUTOUR de l'îlot (original des pixels retirés à ≤ 2 px) ; sans original on retombe sur les clés
				let near = false;
				if (orig) {
					let br = 0;
					let bgc = 0;
					let bb = 0;
					let bn = 0;
					for (const p of comp) {
						const x = p % w;
						const y = (p - x) / w;
						for (let dy = -2; dy <= 2; dy++) {
							const yy = y + dy;
							if (yy < 0 || yy >= h) continue;
							for (let dx = -2; dx <= 2; dx++) {
								const xx = x + dx;
								if (xx < 0 || xx >= w) continue;
								const q = yy * w + xx;
								if (solid[q] || orig[q * 4 + 3] < 16) continue; // retiré = transparent maintenant, opaque à l'origine
								br += orig[q * 4];
								bgc += orig[q * 4 + 1];
								bb += orig[q * 4 + 2];
								bn++;
							}
						}
					}
					if (bn) near = (mr - br / bn) ** 2 + (mg - bgc / bn) ** 2 + (mb - bb / bn) ** 2 <= keyDist2;
				} else {
					for (const k of keys) {
						if ((mr - k.r) ** 2 + (mg - k.g) ** 2 + (mb - k.b) ** 2 <= keyDist2) { near = true; break; }
					}
				}
				if (!near) continue;
			}
			for (const p of comp) d[p * 4 + 3] = 0;
			islands++;
		} else if (orig && !touchesEdge) {
			for (const p of comp) d[p * 4 + 3] = orig[p * 4 + 3];
			holes++;
		}
	}
	return { islands, holes };
}

/** Seuil retenu pour le nettoyage automatique (Daniel 09.10 : « il faut le faire d'office ») : plus prudent que
 *  defaultSpeckleArea pour ne pas manger le point d'un « i » — 0,002 % de l'image, au moins 6 px (≈ 80 px sur 2000×2000). */
export function autoSpeckleArea(w: number, h: number): number {
	return Math.max(6, Math.round(w * h * 0.00002));
}

/**
 * Seuil « poussière » en unités PHYSIQUES, pour les analyses à l'échelle d'impression (traits fins, épaississement) :
 * tout îlot d'encre de moins de 0,1 mm² (≈ un point de 0,35 mm) est du bruit de détourage, pas un trait.
 */
export function dustAreaPx(pxPerMm: number): number {
	return Math.max(4, Math.round(0.1 * pxPerMm * pxPerMm));
}

/**
 * Décontamination des bords après retrait de couleur (le « lissage », Daniel 09.10.2026).
 *
 * Un détourage à la tolérance coupe en plein milieu de l'anticrénelage du logo : les pixels mélangés
 * « encre + fond » qui restent sont 100 % opaques avec une couleur délavée → bords en escalier et halo clair.
 * Pour chaque pixel opaque collé à une zone retirée (2 pixels de profondeur), on estime sa part de fond
 * en projetant sa couleur sur le segment [couleur intérieure → couleur retirée] : cette part devient de la
 * transparence et le pixel reprend la couleur intérieure. Un pixel qui n'est PAS un mélange des deux
 * (résidu trop grand) n'est pas touché, et un trait trop fin pour avoir un intérieur reste tel quel :
 * rien n'est érodé, contrairement à un flou.
 * Retourne le nombre de pixels corrigés. Modifie `d` en place ; `orig` = image avant retrait.
 */
export function defringeEdges(
	d: Uint8ClampedArray,
	orig: Uint8ClampedArray,
	w: number,
	h: number,
	keys: { r: number; g: number; b: number }[],
): number {
	if (!keys.length) return 0;
	const n = w * h;
	// 0 = transparent, 1 = opaque intérieur, 2 = bord (≤ 2 px d'une zone retirée)
	const cls = new Uint8Array(n);
	const removed = new Uint8Array(n);
	for (let i = 0; i < n; i++) {
		const a = d[i * 4 + 3];
		cls[i] = a >= 16 ? 1 : 0;
		if (a < 16 && orig[i * 4 + 3] >= 16) removed[i] = 1;
	}
	const ring = (from: Uint8Array): Uint8Array => {
		const out = new Uint8Array(n);
		for (let y = 0; y < h; y++) {
			for (let x = 0; x < w; x++) {
				const i = y * w + x;
				if (cls[i] !== 1) continue;
				let near = false;
				for (let dy = -1; dy <= 1 && !near; dy++) {
					const yy = y + dy;
					if (yy < 0 || yy >= h) continue;
					for (let dx = -1; dx <= 1; dx++) {
						const xx = x + dx;
						if (xx < 0 || xx >= w) continue;
						if (from[yy * w + xx]) { near = true; break; }
					}
				}
				if (near) out[i] = 1;
			}
		}
		return out;
	};
	const ring1 = ring(removed);
	for (let i = 0; i < n; i++) if (ring1[i]) cls[i] = 2;
	const ring2 = ring(ring1);
	for (let i = 0; i < n; i++) if (ring2[i]) cls[i] = 2;

	// couleurs lues sur une copie : les corrections ne se contaminent pas entre elles
	const src = new Uint8ClampedArray(d);
	let fixed = 0;
	const R = 3; // fenêtre 7×7 pour la couleur intérieure
	for (let y = 0; y < h; y++) {
		for (let x = 0; x < w; x++) {
			const i = y * w + x;
			if (cls[i] !== 2) continue;
			const cr = src[i * 4];
			const cg = src[i * 4 + 1];
			const cb = src[i * 4 + 2];
			// couleur retirée = celle qui a VRAIMENT été enlevée à côté de ce pixel (moyenne, dans l'original, des pixels retirés de la
			// fenêtre 7×7). Daniel 09.10 : avec « noir du tour en zone » puis « blanc partout », prendre « la clé la plus proche du pixel »
			// faisait passer tout trait noir collé au blanc enlevé pour « 100 % noir retiré » → effacé.
			let kr = 0;
			let kg = 0;
			let kb = 0;
			let kn = 0;
			for (let dy = -R; dy <= R; dy++) {
				const yy = y + dy;
				if (yy < 0 || yy >= h) continue;
				for (let dx = -R; dx <= R; dx++) {
					const xx = x + dx;
					if (xx < 0 || xx >= w) continue;
					const j = yy * w + xx;
					if (!removed[j]) continue;
					kr += orig[j * 4];
					kg += orig[j * 4 + 1];
					kb += orig[j * 4 + 2];
					kn++;
				}
			}
			if (!kn) continue;
			const k = { r: kr / kn, g: kg / kn, b: kb / kn };
			// couleur intérieure = moyenne des pixels intérieurs de la fenêtre ; un trait trop fin pour en avoir
			// prend pour référence le pixel voisin le plus éloigné de la couleur retirée (son cœur le moins délavé)
			let sr = 0;
			let sg = 0;
			let sb = 0;
			let cnt = 0;
			let farD = -1;
			let farJ = -1;
			for (let dy = -R; dy <= R; dy++) {
				const yy = y + dy;
				if (yy < 0 || yy >= h) continue;
				for (let dx = -R; dx <= R; dx++) {
					const xx = x + dx;
					if (xx < 0 || xx >= w) continue;
					const j = yy * w + xx;
					if (cls[j] === 1) {
						sr += src[j * 4];
						sg += src[j * 4 + 1];
						sb += src[j * 4 + 2];
						cnt++;
					} else if (cls[j] === 2 && !cnt) {
						const dd = (src[j * 4] - k.r) ** 2 + (src[j * 4 + 1] - k.g) ** 2 + (src[j * 4 + 2] - k.b) ** 2;
						if (dd > farD) { farD = dd; farJ = j; }
					}
				}
			}
			let ir: number;
			let ig: number;
			let ib: number;
			if (cnt) {
				ir = sr / cnt;
				ig = sg / cnt;
				ib = sb / cnt;
			} else if (farJ >= 0) {
				ir = src[farJ * 4];
				ig = src[farJ * 4 + 1];
				ib = src[farJ * 4 + 2];
			} else continue;
			const vr = k.r - ir;
			const vg = k.g - ig;
			const vb = k.b - ib;
			const vv = vr * vr + vg * vg + vb * vb;
			if (vv < 40 * 40) continue; // le dessin lui-même est proche de la couleur retirée : rien à démêler
			let t = ((cr - ir) * vr + (cg - ig) * vg + (cb - ib) * vb) / vv;
			if (t <= 0.02) continue; // pas de fond dans ce pixel
			if (t > 1) t = 1;
			const rr = cr - (ir + t * vr);
			const rg = cg - (ig + t * vg);
			const rb = cb - (ib + t * vb);
			if (rr * rr + rg * rg + rb * rb > 48 * 48) continue; // pas un mélange encre/fond : on laisse
			const o = i * 4;
			d[o] = Math.round(ir);
			d[o + 1] = Math.round(ig);
			d[o + 2] = Math.round(ib);
			d[o + 3] = t >= 0.92 ? 0 : Math.round(d[o + 3] * (1 - t));
			fixed++;
		}
	}
	return fixed;
}
